import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Loader2, LogOut, Inbox, Phone, Mail, MapPin, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "alba_admin_token";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      toast.success("Accesso effettuato");
      onLogin(data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#F5F6F2]">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-white border border-[#DBDBDA] rounded-3xl p-8 md:p-10">
        <div className="font-serif text-3xl text-[#2E3131] mb-1">Area riservata</div>
        <p className="text-sm text-[#5C6360] mb-8">Alba Immobili · gestione contatti</p>
        <label className="overline text-[#5C6360] mb-2 block">Email</label>
        <input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full bg-transparent border-b border-[#DBDBDA] py-3 mb-6 text-[#2E3131] focus:border-[#21463E] outline-none transition-colors duration-300" placeholder="info@albaimmobili.it" />
        <label className="overline text-[#5C6360] mb-2 block">Password</label>
        <input data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-transparent border-b border-[#DBDBDA] py-3 mb-8 text-[#2E3131] focus:border-[#21463E] outline-none transition-colors duration-300" placeholder="••••••••" />
        <button data-testid="admin-login-submit" type="submit" disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#21463E] text-[#F5F6F2] rounded-full py-3.5 text-sm hover:bg-[#2E3131] transition-colors duration-300 disabled:opacity-60">
          {loading ? <><Loader2 size={18} className="animate-spin" /> Accesso...</> : "Accedi"}
        </button>
      </form>
      <Toaster position="bottom-right" />
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(data.leads);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Sessione scaduta");
        onLogout();
      } else {
        toast.error("Errore nel caricamento");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-[#F5F6F2]">
      <header className="sticky top-0 z-10 bg-[#F5F6F2]/80 backdrop-blur-xl border-b border-[#DBDBDA]">
        <div className="max-w-[1200px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif text-2xl text-[#2E3131]">Alba<span className="text-[#21463E]">.</span></span>
            <span className="overline text-[#5C6360] hidden sm:inline">Contatti ricevuti</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 text-sm text-[#5C6360] hover:text-[#2E3131] px-4 py-2 rounded-full border border-[#DBDBDA] hover:border-[#21463E]/40 transition-colors duration-300">
              <RefreshCw size={15} /> Aggiorna
            </button>
            <button data-testid="admin-logout" onClick={onLogout} className="inline-flex items-center gap-2 text-sm text-[#F5F6F2] bg-[#2E3131] px-4 py-2 rounded-full hover:bg-[#21463E] transition-colors duration-300">
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex items-baseline gap-3 mb-8">
          <h1 className="font-serif text-4xl text-[#2E3131]">Richieste</h1>
          <span data-testid="leads-count" className="text-sm text-[#5C6360]">{leads.length} totali</span>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[#5C6360] py-20 justify-center"><Loader2 className="animate-spin" size={20} /> Caricamento...</div>
        ) : leads.length === 0 ? (
          <div data-testid="leads-empty" className="flex flex-col items-center gap-3 text-[#5C6360] py-24 text-center">
            <Inbox size={40} strokeWidth={1.3} />
            <p>Nessuna richiesta ricevuta al momento.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="leads-list">
            {leads.map((l, i) => (
              <article key={l.id || i} data-testid={`lead-card-${i}`} className="bg-white border border-[#DBDBDA] rounded-2xl p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="font-serif text-2xl text-[#2E3131]">{l.nome}</div>
                  <span className="text-xs text-[#5C6360] shrink-0">{fmtDate(l.created_at)}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <a href={`tel:${l.telefono}`} className="flex items-center gap-2 text-[#2E3131] hover:text-[#21463E] transition-colors duration-300"><Phone size={15} /> {l.telefono}</a>
                  <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-[#2E3131] hover:text-[#21463E] transition-colors duration-300"><Mail size={15} /> {l.email}</a>
                  {l.zona && <div className="flex items-center gap-2 text-[#5C6360]"><MapPin size={15} /> {l.zona}</div>}
                </div>
                {l.messaggio && <p className="mt-4 pt-4 border-t border-[#DBDBDA] text-[#5C6360] leading-relaxed">{l.messaggio}</p>}
              </article>
            ))}
          </div>
        )}
      </main>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  if (!token) return <LoginForm onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
